import React, { useState } from 'react';
import { Card, LoadingSpinner } from 'components/common';
import { FiPieChart } from 'react-icons/fi';
import PropTypes from 'prop-types';

const getErrorMessage = (error) => {
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && error.error) return error.error;
  return 'An error occurred';
};

const COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444',
  '#A855F7', '#EC4899', '#06B6D4', '#6366F1'
];
const getColor = (index) => COLORS[index % COLORS.length];

const formatStatus = (name) =>
  (name || 'Unknown').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const HtmlTooltip = ({ visible, x, y, item, color }) => {
  if (!visible || !item) return null;
  return (
    <div
      style={{
        position: 'fixed',
        left: x + 14,
        top: y - 10,
        zIndex: 9999,
        pointerEvents: 'none',
        background: 'white',
        border: `2px solid ${color}`,
        borderRadius: '10px',
        padding: '10px 14px',
        boxShadow: '0 10px 25px rgba(0,0,0,0.12)',
        minWidth: '130px'
      }}
    >
      <p className="tw-font-bold tw-text-gray-900 tw-text-sm tw-mb-1">{item.name}</p>
      <p className="tw-text-2xl tw-font-extrabold" style={{ color }}>{item.value}</p>
      <p className="tw-text-xs tw-text-gray-500 tw-mt-0.5">{item.percentage}% of total</p>
    </div>
  );
};

const sliceShape = PropTypes.shape({
  name:       PropTypes.string,
  value:      PropTypes.number,
  percentage: PropTypes.number
});

HtmlTooltip.propTypes = {
  visible: PropTypes.bool,
  x:       PropTypes.number,
  y:       PropTypes.number,
  item:    sliceShape,
  color:   PropTypes.string
};

const DonutChart = ({ data, size = 300 }) => {
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, index: null });

  const outerR = size / 2 - 12;
  const innerR = outerR * 0.55;
  const cx     = size / 2;
  const cy     = size / 2;

  const total = data.reduce((s, d) => s + d.value, 0);
  let angle   = -Math.PI / 2;

  const slices = data.map((item, index) => {
    const sweep      = (item.value / total) * 2 * Math.PI;
    const startAngle = angle;
    const endAngle   = angle + sweep;
    const midAngle   = startAngle + sweep / 2;
    angle            = endAngle;

    const x1 = cx + outerR * Math.cos(startAngle);
    const y1 = cy + outerR * Math.sin(startAngle);
    const x2 = cx + outerR * Math.cos(endAngle);
    const y2 = cy + outerR * Math.sin(endAngle);
    const xi1 = cx + innerR * Math.cos(endAngle);
    const yi1 = cy + innerR * Math.sin(endAngle);
    const xi2 = cx + innerR * Math.cos(startAngle);
    const yi2 = cy + innerR * Math.sin(startAngle);

    const large = sweep > Math.PI ? 1 : 0;

    const path = [
      `M ${x1} ${y1}`,
      `A ${outerR} ${outerR} 0 ${large} 1 ${x2} ${y2}`,
      `L ${xi1} ${yi1}`,
      `A ${innerR} ${innerR} 0 ${large} 0 ${xi2} ${yi2}`,
      'Z'
    ].join(' ');

    // label position (mid radius)
    const labelR = outerR * 0.76;
    const lx = cx + labelR * Math.cos(midAngle);
    const ly = cy + labelR * Math.sin(midAngle);

    return { path, color: getColor(index), lx, ly, ...item };
  });

  const isHovered = (index) => tooltip.index === index;

  return (
    <>
      <HtmlTooltip
        visible={tooltip.visible}
        x={tooltip.x}
        y={tooltip.y}
        item={tooltip.index !== null ? slices[tooltip.index] : null}
        color={tooltip.index !== null ? slices[tooltip.index]?.color : '#000'}
      />
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="tw-w-full">
        {slices.map((slice, i) => (
          <g
            key={i}
            onMouseMove={(e) => setTooltip({ visible: true, x: e.clientX, y: e.clientY, index: i })}
            onMouseLeave={() => setTooltip({ visible: false, x: 0, y: 0, index: null })}
            style={{ cursor: 'pointer' }}
          >
            <path
              d={slice.path}
              fill={slice.color}
              stroke="white"
              strokeWidth={isHovered(i) ? 3 : 2}
              opacity={isHovered(i) ? 1 : 0.88}
              style={{
                filter: isHovered(i) ? 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))' : 'none',
                transition: 'all 0.2s ease'
              }}
            />
            {/* count label inside slice */}
            {slice.value > 0 && ((slice.value / total) > 0.05) && (
              <text
                x={slice.lx}
                y={slice.ly}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize="12"
                fontWeight="700"
                fill="white"
                pointerEvents="none"
              >
                {slice.value}
              </text>
            )}
          </g>
        ))}

        {/* Center label */}
        <text x={cx} y={cy - 8} textAnchor="middle" fontSize="24" fontWeight="800" fill="#111827">
          {total}
        </text>
        <text x={cx} y={cy + 14} textAnchor="middle" fontSize="11" fill="#6b7280">
          Total CO#
        </text>
      </svg>
    </>
  );
};

DonutChart.propTypes = {
  data: PropTypes.arrayOf(sliceShape).isRequired,
  size: PropTypes.number
};

const StatusChart = ({ data, loading, error }) => {
  if (loading) {
    return (
      <Card className="tw-p-6 tw-border tw-border-gray-200">
        <div className="tw-flex tw-justify-center tw-items-center tw-py-12">
          <LoadingSpinner size="lg" />
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="tw-bg-red-50 tw-border tw-border-red-200 tw-p-6">
        <p className="tw-text-red-600 tw-font-semibold">Error loading status distribution</p>
        <p className="tw-text-red-500 tw-text-sm">{getErrorMessage(error)}</p>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card className="tw-p-6 tw-border tw-border-gray-200">
        <p className="tw-text-gray-500 tw-text-center tw-py-8">No data available</p>
      </Card>
    );
  }

  const chartData  = data.map(item => ({ name: formatStatus(item.status), value: item.count, percentage: item.percentage }));
  const totalCOs   = chartData.reduce((s, d) => s + d.value, 0);
  const mostCommon = chartData[0]?.name || 'N/A';

  return (
    <Card className="tw-bg-gradient-to-br tw-from-purple-50 tw-via-white tw-to-primary-50 tw-border tw-border-gray-200 tw-shadow-sm hover:tw-shadow-md tw-transition-shadow">
      <div className="tw-p-6 tw-border-b tw-border-gray-200">
        <h3 className="tw-text-lg tw-font-bold tw-text-gray-900 tw-flex tw-items-center tw-gap-2">
          <FiPieChart className="tw-text-purple-600" size={20} />
          Change Order Status Distribution
        </h3>
        <p className="tw-text-sm tw-text-gray-600 tw-mt-1">
          Overview of change orders by current status — hover slices for details
        </p>
      </div>

      <div className="tw-p-6">
        <div className="tw-flex tw-flex-col lg:tw-flex-row tw-gap-8 tw-items-center">
          {/* Donut Chart */}
          <div className="tw-flex-shrink-0 tw-flex tw-justify-center">
            <div className="tw-bg-white tw-rounded-lg tw-p-4 tw-shadow-sm" style={{ width: 280 }}>
              <DonutChart data={chartData} size={260} />
            </div>
          </div>

          {/* Legend */}
          <div className="tw-flex-1 tw-w-full">
            <h4 className="tw-font-semibold tw-text-gray-700 tw-text-sm tw-uppercase tw-tracking-wider tw-mb-3">
              Status Breakdown
            </h4>
            <div className="tw-space-y-2">
              {chartData.map((item, i) => (
                <div
                  key={i}
                  className="tw-flex tw-items-center tw-justify-between tw-p-2.5 tw-rounded hover:tw-bg-gray-50 tw-transition-colors tw-border tw-border-transparent hover:tw-border-gray-200"
                >
                  <div className="tw-flex tw-items-center tw-gap-3 tw-min-w-0">
                    <div
                      className="tw-w-3.5 tw-h-3.5 tw-rounded-xl tw-flex-shrink-0"
                      style={{ backgroundColor: getColor(i) }}
                    />
                    <span className="tw-font-medium tw-text-gray-800 tw-text-sm tw-truncate">
                      {item.name}
                    </span>
                  </div>
                  <div className="tw-flex tw-items-center tw-gap-2 tw-flex-shrink-0 tw-ml-3">
                    <div
                      className="tw-h-1.5 tw-rounded-xl"
                      style={{
                        width: `${Math.max(item.percentage, 3)}px`,
                        maxWidth: '60px',
                        backgroundColor: getColor(i),
                        opacity: 0.4
                      }}
                    />
                    <span className="tw-text-sm tw-font-bold tw-text-gray-900 tw-w-6 tw-text-right">
                      {item.value}
                    </span>
                    <span className="tw-text-xs tw-text-gray-400 tw-w-12 tw-text-right">
                      ({item.percentage}%)
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="tw-mt-6 tw-pt-6 tw-border-t tw-border-gray-200">
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4">
            <div className="tw-p-4 tw-rounded tw-bg-gradient-to-br tw-from-blue-50 tw-to-blue-100 tw-border tw-border-blue-200">
              <p className="tw-text-xs tw-text-blue-600 tw-font-semibold tw-uppercase tw-tracking-wide">Total CO#</p>
              <p className="tw-text-2xl tw-font-bold tw-text-blue-900 tw-mt-1">{totalCOs}</p>
              <p className="tw-text-xs tw-text-blue-500 tw-mt-0.5">Across all statuses</p>
            </div>
            <div className="tw-p-4 tw-rounded tw-bg-gradient-to-br tw-from-purple-50 tw-to-purple-100 tw-border tw-border-purple-200">
              <p className="tw-text-xs tw-text-purple-600 tw-font-semibold tw-uppercase tw-tracking-wide">Status Types</p>
              <p className="tw-text-2xl tw-font-bold tw-text-purple-900 tw-mt-1">{chartData.length}</p>
              <p className="tw-text-xs tw-text-purple-500 tw-mt-0.5">Different statuses</p>
            </div>
            <div className="tw-p-4 tw-rounded tw-bg-gradient-to-br tw-from-green-50 tw-to-green-100 tw-border tw-border-green-200">
              <p className="tw-text-xs tw-text-green-600 tw-font-semibold tw-uppercase tw-tracking-wide">Most Common</p>
              <p className="tw-text-sm tw-font-bold tw-text-green-900 tw-mt-1 tw-truncate tw-capitalize">{mostCommon}</p>
              <p className="tw-text-xs tw-text-green-500 tw-mt-0.5">Primary status</p>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

StatusChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      status:     PropTypes.string.isRequired,
      count:      PropTypes.number.isRequired,
      percentage: PropTypes.number.isRequired
    })
  ),
  loading: PropTypes.bool,
  error:   PropTypes.oneOfType([PropTypes.string, PropTypes.object])
};

StatusChart.defaultProps = {
  data:    [],
  loading: false,
  error:   null
};

export default StatusChart;
